import { useMedia, useT } from "@/lib/i18n";
import charlene from "@/assets/team/charlene.png";
import lola from "@/assets/team/lola.png";
import beatriz from "@/assets/team/beatriz.png";
import gimena from "@/assets/team/gimena.png";
import javier from "@/assets/team/javier.png";
import mariella from "@/assets/team/mariella.png";
import antonio from "@/assets/team/antonio.png";
import isabel from "@/assets/team/isabel.png";

export type TeamMember = {
  slug: string;
  name: string;
  role: string;
  shortRole: string;
  bio: string;
  specialties: string[];
  portrait: { url: string; alt: string };
  featured: boolean;
};

/**
 * Returns the team in render order. All strings flow through tk() and all
 * portraits through useMedia(), so the backoffice can override every value
 * via i18n-seed.json / media-keys.json without a database.
 *
 * NOTE: hooks are called in a fixed order — never wrap calls in conditionals.
 */
export function useTeam(): TeamMember[] {
  const { tk } = useT();

  const charleneImg = useMedia("team.charlene.portrait", charlene, "Charlene Ramos Rodrigues");
  const gimenaImg = useMedia("team.gimena.portrait", gimena, "Gimena Fonseca Ferreira");
  const beatrizImg = useMedia("team.beatriz.portrait", beatriz, "Beatriz Navarro Simón");
  const javierImg = useMedia("team.javier.portrait", javier, "Javier García Rodríguez");
  const antonioImg = useMedia("team.antonio.portrait", antonio, "Antonio Rafael Plaguezuelo Magrit");
  const isabelImg = useMedia("team.isabel.portrait", isabel, "Isabel María Mira Sosa");
  const lolaImg = useMedia("team.lola.portrait", lola, "Lola Capote");
  const mariellaImg = useMedia("team.mariella.portrait", mariella, "Mariella Di Guardia");

  return [
    {
      slug: "charlene-ramos-rodrigues",
      name: tk("team.charlene.name", "Charlene Ramos Rodrigues"),
      role: tk("team.charlene.role", "Fundadora y Directora · Abogada Penalista"),
      shortRole: tk("team.charlene.shortRole", "Fundadora & Directora"),
      bio: tk(
        "team.charlene.bio",
        "Considerada una de las mejores abogadas penalistas de España, Charlene lidera RR Lex Juristas con una visión moderna, humana y rigurosa de la abogacía. Más de 3.000 casos resueltos y una sólida trayectoria en procesos de alto perfil avalan su prestigio.",
      ),
      specialties: [
        tk("team.charlene.specialty.1", "Derecho Penal"),
        tk("team.charlene.specialty.2", "Defensa en juicios mediáticos"),
        tk("team.charlene.specialty.3", "Mediación"),
      ],
      portrait: charleneImg,
      featured: true,
    },
    {
      slug: "gimena-fonseca-ferreira",
      name: tk("team.gimena.name", "Gimena Fonseca Ferreira"),
      role: tk("team.gimena.role", "Abogada · Civil, Familia, Laboral y Defensa de los Animales"),
      shortRole: tk("team.gimena.shortRole", "Familia & Animales"),
      bio: tk(
        "team.gimena.bio",
        "Gimena combina la práctica del derecho civil, de familia y laboral con un compromiso pionero en la defensa jurídica de los animales, aportando sensibilidad y rigor a cada caso.",
      ),
      specialties: [
        tk("team.gimena.specialty.1", "Derecho Civil"),
        tk("team.gimena.specialty.2", "Derecho de Familia"),
      ],
      portrait: gimenaImg,
      featured: false,
    },
    {
      slug: "beatriz-navarro-simon",
      name: tk("team.beatriz.name", "Beatriz Navarro Simón"),
      role: tk("team.beatriz.role", "Abogada · Derecho Internacional Privado e Inversión Extranjera"),
      shortRole: tk("team.beatriz.shortRole", "Derecho Internacional"),
      bio: tk(
        "team.beatriz.bio",
        "Beatriz asesora a clientes internacionales que invierten o residen en España: compraventas inmobiliarias, herencias transfronterizas, fiscalidad internacional y planificación patrimonial con visión global.",
      ),
      specialties: [
        tk("team.beatriz.specialty.1", "Derecho Internacional Privado"),
        tk("team.beatriz.specialty.2", "Inversión extranjera"),
        tk("team.beatriz.specialty.3", "Compraventas inmobiliarias"),
        tk("team.beatriz.specialty.4", "Fiscalidad internacional"),
      ],
      portrait: beatrizImg,
      featured: false,
    },
    {
      slug: "javier-garcia-rodriguez",
      name: tk("team.javier.name", "Javier García Rodríguez"),
      role: tk("team.javier.role", "Abogado · Penal, Civil y Tributario"),
      shortRole: tk("team.javier.shortRole", "Penal, Civil & Tributario"),
      bio: tk(
        "team.javier.bio",
        "Javier interviene en procedimientos penales, asuntos civiles complejos y litigios tributarios, ofreciendo una estrategia jurídica integral con foco en resultados.",
      ),
      specialties: [
        tk("team.javier.specialty.1", "Derecho Penal"),
        tk("team.javier.specialty.2", "Derecho Civil"),
        tk("team.javier.specialty.3", "Derecho Tributario"),
      ],
      portrait: javierImg,
      featured: false,
    },
    {
      slug: "antonio-rafael-plaguezuelo-magrit",
      name: tk("team.antonio.name", "Antonio Rafael Plaguezuelo Magrit"),
      role: tk("team.antonio.role", "Procurador de los Tribunales"),
      shortRole: tk("team.antonio.shortRole", "Procurador"),
      bio: tk(
        "team.antonio.bio",
        "Antonio Rafael actúa como procurador de los tribunales, garantizando la correcta representación procesal del despacho y la fluidez de cada procedimiento ante juzgados y tribunales.",
      ),
      specialties: [
        tk("team.antonio.specialty.1", "Representación procesal"),
        tk("team.antonio.specialty.2", "Tramitación judicial"),
        tk("team.antonio.specialty.3", "Coordinación con tribunales"),
      ],
      portrait: antonioImg,
      featured: false,
    },
    {
      slug: "isabel-maria-mira-sosa",
      name: tk("team.isabel.name", "Isabel María Mira Sosa"),
      role: tk("team.isabel.role", "Procuradora de los Tribunales"),
      shortRole: tk("team.isabel.shortRole", "Procuradora"),
      bio: tk(
        "team.isabel.bio",
        "Isabel María ejerce como procuradora de los tribunales, asegurando la representación procesal del despacho y la correcta tramitación de cada procedimiento ante juzgados y tribunales.",
      ),
      specialties: [
        tk("team.isabel.specialty.1", "Representación procesal"),
        tk("team.isabel.specialty.2", "Tramitación judicial"),
        tk("team.isabel.specialty.3", "Coordinación con tribunales"),
      ],
      portrait: isabelImg,
      featured: false,
    },
    {
      slug: "lola-capote",
      name: tk("team.lola.name", "Lola Capote"),
      role: tk("team.lola.role", "Asesora Fiscal y Contable"),
      shortRole: tk("team.lola.shortRole", "Asesora Fiscal & Contable"),
      bio: tk(
        "team.lola.bio",
        "Lola coordina el área fiscal y contable del despacho, ofreciendo a clientes particulares y empresas una planificación tributaria clara, rigurosa y adaptada a cada situación patrimonial.",
      ),
      specialties: [
        tk("team.lola.specialty.1", "Asesoría fiscal"),
        tk("team.lola.specialty.2", "Contabilidad"),
        tk("team.lola.specialty.3", "Planificación tributaria"),
      ],
      portrait: lolaImg,
      featured: false,
    },
    {
      slug: "mariella-di-guardia",
      name: tk("team.mariella.name", "Mariella Di Guardia"),
      role: tk("team.mariella.role", "Asesora Fiscal y Contable"),
      shortRole: tk("team.mariella.shortRole", "Asesora Fiscal & Contable"),
      bio: tk(
        "team.mariella.bio",
        "Mariella trabaja junto a Lola Capote en el área fiscal y contable del despacho, ofreciendo a clientes particulares y empresas una planificación tributaria clara, rigurosa y adaptada a cada situación patrimonial.",
      ),
      specialties: [
        tk("team.mariella.specialty.1", "Asesoría fiscal"),
        tk("team.mariella.specialty.2", "Contabilidad"),
        tk("team.mariella.specialty.3", "Planificación tributaria"),
      ],
      portrait: mariellaImg,
      featured: false,
    },
  ];
}
